import React, { useState, useEffect, useCallback } from "react";
import { db } from "../../../firebase/firebaseConfig";
import { collection, query, where, getDocs, doc, setDoc, addDoc } from "firebase/firestore";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { logClassActivity } from "../api/activityLogger";
import { Button } from "../../../components/ui/form/button";
import { FiCalendar, FiUserCheck, FiClipboard, FiSave } from "react-icons/fi";

const attendanceStatus = [
  { value: "present", label: "Có mặt" },
  { value: "absent", label: "Vắng" },
  { value: "late", label: "Đi muộn" },
  { value: "excused", label: "Có phép" },
];

// Attendance History
function AttendanceHistory({ history, loading, students }) {
  if (loading)
    return (
      <div className="my-6 text-text2 text-sm flex items-center gap-2">
        <FiClipboard className="animate-spin text-primary" /> Đang tải lịch sử điểm danh...
      </div>
    );
  if (!history.length)
    return (
      <div className="my-6 text-text2 text-sm">Chưa có lịch sử điểm danh.</div>
    );

  return (
    <div className="mt-8 bg-card border border-border rounded-xl p-4 shadow">
      <h4 className="font-semibold mb-2 flex items-center gap-2 text-menu">
        <FiClipboard className="text-primary" /> Lịch sử điểm danh gần đây
      </h4>
      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-max w-full text-sm">
          <thead>
            <tr className="bg-bg">
              <th className="px-2 py-1 text-left text-menu">Ngày</th>
              {students.map(s => (
                <th key={s.id} className="px-2 py-1 text-center text-menu">
                  {s.fullName || `${s.firstName} ${s.lastName}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map(h => (
              <tr key={h.date}>
                <td className="px-2 py-1 font-semibold">{dayjs(h.date).format("DD/MM/YYYY")}</td>
                {students.map(s => {
                  const st = h.students.find(x => x.studentId === s.id);
                  let symbol = "⬜️";
                  if (st?.status === "present") symbol = "✅";
                  else if (st?.status === "absent") symbol = "❌";
                  else if (st?.status === "late") symbol = "⏰";
                  else if (st?.status === "excused") symbol = "📝";
                  return <td key={s.id} className="text-center">{symbol}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Attendance Report
function AttendanceReport({ classId, students }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    const fetchHistory = async () => {
      const q = query(collection(db, "attendance"), where("classId", "==", classId));
      const snap = await getDocs(q);
      setHistory(snap.docs.map(doc => doc.data()));
    };
    fetchHistory();
  }, [classId]);

  const summary = students.map(s => {
    let present = 0, absent = 0, late = 0, excused = 0;
    history.forEach(h => {
      const st = h.students.find(x => x.studentId === s.id);
      if (!st) return;
      if (st.status === "present") present++;
      if (st.status === "absent") absent++;
      if (st.status === "late") late++;
      if (st.status === "excused") excused++;
    });
    return { ...s, present, absent, late, excused, total: history.length };
  });

  return (
    <div className="mt-8 bg-card border border-border rounded-xl p-4 shadow">
      <h4 className="font-semibold mb-2 flex items-center gap-2 text-menu">
        <FiUserCheck className="text-success" /> Báo cáo chuyên cần
      </h4>
      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-max w-full text-sm">
          <thead>
            <tr className="bg-bg">
              <th className="px-2 py-1 text-left text-menu">Học viên</th>
              <th className="px-2 py-1 text-center">Có mặt</th>
              <th className="px-2 py-1 text-center">Vắng</th>
              <th className="px-2 py-1 text-center">Muộn</th>
              <th className="px-2 py-1 text-center">Có phép</th>
              <th className="px-2 py-1 text-center">Tỷ lệ chuyên cần</th>
            </tr>
          </thead>
          <tbody>
            {summary.map(s => (
              <tr key={s.id}>
                <td className="px-2 py-1">{s.fullName || `${s.firstName} ${s.lastName}`}</td>
                <td className="px-2 py-1 text-center">{s.present}</td>
                <td className="px-2 py-1 text-center">{s.absent}</td>
                <td className="px-2 py-1 text-center">{s.late}</td>
                <td className="px-2 py-1 text-center">{s.excused}</td>
                <td className="px-2 py-1 text-center font-semibold">
                  {s.total > 0 ? Math.round((s.present / s.total) * 100) + "%" : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// MAIN TAB ATTENDANCE
export default function TabAttendance({ classInfo, students }) {
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const studentsToShow = students.filter(
    s =>
      (s.firstName + " " + s.lastName).toLowerCase().includes(search.toLowerCase()) ||
      (s.email || "").toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      const q = query(
        collection(db, "attendance"),
        where("classId", "==", classInfo.id),
        where("date", "==", date)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const att = snap.docs[0].data();
        const attMap = {};
        att.students.forEach(s => {
          attMap[s.studentId] = { status: s.status, note: s.note || "" };
        });
        setAttendance(attMap);
      } else {
        const attMap = {};
        students.forEach(s => {
          attMap[s.id] = { status: "present", note: "" };
        });
        setAttendance(attMap);
      }
      setLoading(false);
    };
    fetchAttendance();
  }, [classInfo.id, date, students]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    const q = query(collection(db, "attendance"), where("classId", "==", classInfo.id));
    const snap = await getDocs(q);
    const docs = snap.docs.map(doc => doc.data()).sort((a, b) => b.date.localeCompare(a.date));
    setHistory(docs.slice(0, 7));
    setHistoryLoading(false);
  }, [classInfo.id]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleChange = (studentId, status) => {
    setAttendance((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], status },
    }));
  };
  const handleNote = (studentId, note) => {
    setAttendance((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], note },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    const attList = Object.entries(attendance).map(([studentId, val]) => ({
      studentId,
      status: val.status,
      note: val.note,
    }));
    const docId = `${classInfo.id}_${date}`;
    await setDoc(doc(db, "attendance", docId), {
      classId: classInfo.id,
      date,
      students: attList,
      updatedAt: new Date(),
    });

    const counts = attList.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {});
    await logClassActivity({
      classId: classInfo.id,
      type: "attendance_updated",
      severity: (counts.absent || 0) > 0 || (counts.late || 0) > 0 ? "warning" : "normal",
      targetId: docId,
      targetTitle: date,
      message: `Đã lưu điểm danh ngày ${date}: ${counts.present || 0} có mặt, ${counts.absent || 0} vắng, ${counts.late || 0} đi muộn, ${counts.excused || 0} có phép.`,
      metadata: { date, counts },
    });
    const absents = attList.filter(s => s.status === "absent");
    for (const student of absents) {
      await addDoc(collection(db, "notifications"), {
        toUserId: student.studentId,
        type: "attendance",
        title: "Thông báo nghỉ học",
        message: `Bạn đã nghỉ học ngày ${date}${student.note ? ": " + student.note : ""}`,
        createdAt: new Date(),
        classId: classInfo.id,
      });
    }
    setLoading(false);
    toast.success("Đã lưu điểm danh!");
    fetchHistory();
  };

  return (
    <div className="">
      <div className="flex flex-wrap gap-3 sm:gap-6 items-center mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2 text-primary mb-2">
          <FiCalendar className="text-primary" /> Điểm danh
        </h3>
        <div className="flex items-center gap-2">
          <label className="font-medium">Chọn ngày:</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-border rounded-xl px-2 py-1 bg-card"
          />
        </div>
        <input
          type="text"
          className="border border-border rounded-xl px-2 py-1 bg-card"
          placeholder="Tìm học viên..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 180 }}
        />
        <Button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 rounded-xl font-semibold bg-primary text-text-button hover:brightness-95"
        >
          <FiSave /> {loading ? "Đang lưu..." : "Lưu điểm danh"}
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-[520px] w-full text-sm">
          <thead>
            <tr className="bg-bg">
              <th className="px-2 py-2 text-menu">#</th>
              <th className="px-2 py-2 text-left text-menu">Họ tên</th>
              <th className="px-2 py-2 text-left text-menu">Trạng thái</th>
              <th className="px-2 py-2 text-left text-menu">Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            {studentsToShow.map((s, idx) => (
              <tr key={s.id} className="border-b border-border hover:bg-primary/5">
                <td className="px-2 py-2">{idx + 1}</td>
                <td className="px-2 py-2">{s.fullName || `${s.firstName} ${s.lastName}`}</td>
                <td className="px-2 py-2">
                  <select
                    value={attendance[s.id]?.status || "present"}
                    onChange={e => handleChange(s.id, e.target.value)}
                    className="border border-border rounded-xl px-2 py-1 bg-card"
                  >
                    {attendanceStatus.map(st => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    value={attendance[s.id]?.note || ""}
                    onChange={e => handleNote(s.id, e.target.value)}
                    placeholder="Lý do (nếu vắng)"
                    className="border border-border rounded-xl px-2 py-1 w-full bg-card"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AttendanceHistory history={history} loading={historyLoading} students={students} />
      <AttendanceReport classId={classInfo.id} students={students} />
    </div>
  );
}
